import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, EmptyState, StatusBadge } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  Sparkles, Users, ArrowRight, MapPin, CheckCircle2, Loader2, X, Scale, ShieldCheck,
} from "lucide-react";

const TABS = [
  { id: "services", label: "Implementation services" },
  { id: "lawyers", label: "Find a lawyer" },
  { id: "enquiries", label: "My enquiries" },
];

export default function MarketplacePage() {
  const [tab, setTab] = useState("services");
  const [services, setServices] = useState([]);
  const [lawyers, setLawyers] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [selected, setSelected] = useState(null);   // {kind, item}
  const [form, setForm] = useState({ matter_id: "", contact_email: "", contact_phone: "", notes: "" });
  const [matters, setMatters] = useState([]);
  const [jurisdictionFilter, setJurisdictionFilter] = useState("");
  const [areaFilter, setAreaFilter] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    const [s, l, m, e] = await Promise.all([
      api.get("/marketplace/services").then((r) => r.data),
      api.get("/marketplace/lawyers").then((r) => r.data),
      api.get("/matters").then((r) => r.data).catch(() => []),
      api.get("/marketplace/enquiries").then((r) => r.data).catch(() => []),
    ]);
    setServices(s); setLawyers(l); setMatters(m); setEnquiries(e);
  };
  useEffect(() => { load(); }, []);

  const filteredLawyers = lawyers.filter((l) =>
    (!jurisdictionFilter || l.jurisdictions.some((j) => j.toLowerCase().includes(jurisdictionFilter.toLowerCase()))) &&
    (!areaFilter || l.practice_areas.some((p) => p.toLowerCase().includes(areaFilter.toLowerCase()))),
  );

  const openEnquiry = (kind, item) => {
    setSelected({ kind, item });
    setForm({ matter_id: "", contact_email: "", contact_phone: "", notes: "" });
  };

  const submit = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/marketplace/enquiry", {
        kind: selected.kind,
        target_slug_or_id: selected.kind === "service" ? selected.item.slug : selected.item.id,
        matter_id: form.matter_id || null,
        contact_email: form.contact_email || null,
        contact_phone: form.contact_phone || null,
        notes: form.notes || "",
      });
      toast.success(`Enquiry logged — ref ${data.ticket_ref}. We'll reach out within 1 business day.`);
      setSelected(null);
      load();
      setTab("enquiries");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit enquiry");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="marketplace-page">
      <PageHeader
        eyebrow="Le Marché"
        title="Get help when you need a human."
        description="Fixed-price implementation services delivered by Marianne, and a directory of independent lawyers you can reach for reviews, notices, filings and representation."
      />

      <div className="inline-flex border border-[#E8E3F0] bg-white mb-6" data-testid="marketplace-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm ${tab === t.id ? "bg-[#2A0F5A] text-[#FAFAF8]" : "text-[#2A0F5A] hover:bg-[#FAFAF8]"}`}
            data-testid={`marketplace-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "services" && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E8E3F0] border border-[#E8E3F0]" data-testid="services-grid">
          {services.map((s) => (
            <div key={s.slug} className="bg-white p-6 flex flex-col" data-testid={`service-${s.slug}`}>
              <div className="label-eyebrow text-[#C5A059] mb-2">{s.category}</div>
              <div className="font-serif text-2xl leading-tight mb-2">{s.title}</div>
              <div className="text-sm text-[#2A0F5A]/75 mb-4 flex-1">{s.summary}</div>
              <ul className="text-xs space-y-1 mb-4">
                {s.included.slice(0, 5).map((f, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <CheckCircle2 size={11} strokeWidth={1.6} className="text-[#7B56C9] mt-0.5 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <div className="flex items-end justify-between border-t border-[#E8E3F0] pt-4">
                <div>
                  <div className="font-serif text-3xl">₹{s.price_inr.toLocaleString()}</div>
                  <div className="text-[11px] text-[#4A5568]">{s.duration_note}</div>
                </div>
                <Button onClick={() => openEnquiry("service", s)} size="sm" className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]" data-testid={`service-cta-${s.slug}`}>
                  Enquire <ArrowRight size={12} className="ml-1" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "lawyers" && (
        <div>
          <div className="flex flex-wrap gap-3 mb-6">
            <Input placeholder="Jurisdiction (e.g. Delhi, EU, New York)" value={jurisdictionFilter} onChange={(e) => setJurisdictionFilter(e.target.value)} className="rounded-none border-[#E8E3F0] max-w-xs" data-testid="lawyer-jurisdiction-filter" />
            <Input placeholder="Practice area (e.g. commercial, GDPR)" value={areaFilter} onChange={(e) => setAreaFilter(e.target.value)} className="rounded-none border-[#E8E3F0] max-w-xs" data-testid="lawyer-area-filter" />
          </div>
          {filteredLawyers.length === 0 ? (
            <EmptyState icon={Users} title="No lawyers match those filters" description="Try widening the jurisdiction or practice area." />
          ) : (
            <div className="grid md:grid-cols-2 gap-px bg-[#E8E3F0] border border-[#E8E3F0]" data-testid="lawyers-grid">
              {filteredLawyers.map((l) => (
                <div key={l.id} className="bg-white p-6" data-testid={`lawyer-${l.id}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <div className="font-serif text-2xl leading-tight">{l.name}</div>
                      <div className="label-eyebrow text-[#C5A059] mt-1 flex items-center gap-1"><MapPin size={11} /> {l.location}</div>
                    </div>
                    {l.verified && <div className="flex items-center gap-1 text-[11px] text-emerald-800"><ShieldCheck size={12} /> Verified</div>}
                  </div>
                  <div className="text-sm text-[#2A0F5A]/75 mb-3">{l.bio}</div>
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {l.practice_areas.map((p) => <span key={p} className="text-[10px] px-1.5 py-0.5 border border-[#E8E3F0]">{p}</span>)}
                  </div>
                  <div className="flex flex-wrap gap-3 text-xs text-[#4A5568] mb-4">
                    <span>{l.jurisdictions.join(" · ")}</span>
                    {l.hourly_rate_inr && <span>· ₹{l.hourly_rate_inr.toLocaleString()}/hr</span>}
                    <span>· Bar: {l.bar_number}</span>
                  </div>
                  <Button onClick={() => openEnquiry("lawyer", l)} size="sm" className="rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]" data-testid={`lawyer-cta-${l.id}`}>
                    Request consultation <ArrowRight size={12} className="ml-1" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "enquiries" && (
        <div>
          {enquiries.length === 0 ? (
            <EmptyState icon={Sparkles} title="You haven't raised any enquiries yet" description="Pick a service or a lawyer and open an enquiry." />
          ) : (
            <div className="border border-[#E8E3F0] bg-white" data-testid="enquiries-list">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#E8E3F0]">
                    <th className="text-left px-4 py-3 label-eyebrow">Ref</th>
                    <th className="text-left px-4 py-3 label-eyebrow">Type</th>
                    <th className="text-left px-4 py-3 label-eyebrow">Target</th>
                    <th className="text-left px-4 py-3 label-eyebrow">Status</th>
                    <th className="text-left px-4 py-3 label-eyebrow hidden md:table-cell">Opened</th>
                  </tr>
                </thead>
                <tbody>
                  {enquiries.map((e) => (
                    <tr key={e.id} className="border-b border-[#E8E3F0]" data-testid={`enquiry-row-${e.id}`}>
                      <td className="px-4 py-3 font-mono text-xs">{e.ticket_ref}</td>
                      <td className="px-4 py-3 capitalize">{e.kind}</td>
                      <td className="px-4 py-3">{e.target_slug_or_id}</td>
                      <td className="px-4 py-3"><StatusBadge tone="info">{e.status}</StatusBadge></td>
                      <td className="px-4 py-3 hidden md:table-cell text-xs text-[#4A5568]">{new Date(e.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Enquiry drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 bg-[#2A0F5A]/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setSelected(null)} data-testid="enquiry-modal">
          <div className="bg-white border border-[#E8E3F0] w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="label-eyebrow text-[#C5A059] mb-1">Enquiry · {selected.kind}</div>
                <div className="font-serif text-2xl">{selected.kind === "service" ? selected.item.title : selected.item.name}</div>
              </div>
              <button onClick={() => setSelected(null)} className="text-[#4A5568] hover:text-[#2A0F5A]"><X size={16} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-[#4A5568] block mb-1 label-eyebrow">Link to a matter (optional)</label>
                <select value={form.matter_id} onChange={(e) => setForm({ ...form, matter_id: e.target.value })} className="w-full border border-[#E8E3F0] bg-white px-3 py-2 text-sm" data-testid="enquiry-matter-select">
                  <option value="">No matter</option>
                  {matters.map((m) => (<option key={m.id} value={m.id}>{m.title}</option>))}
                </select>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Input placeholder="Contact email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="rounded-none border-[#E8E3F0]" data-testid="enquiry-email" />
                <Input placeholder="Contact phone (optional)" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className="rounded-none border-[#E8E3F0]" data-testid="enquiry-phone" />
              </div>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={4}
                placeholder="Tell us the context. What are you trying to achieve?"
                className="w-full border border-[#E8E3F0] px-3 py-2 text-sm resize-y"
                data-testid="enquiry-notes"
              />
              <Button onClick={submit} disabled={submitting} className="w-full rounded-none bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]" data-testid="enquiry-submit">
                {submitting ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Submitting…</> : <><Scale size={14} className="mr-1.5" /> Submit enquiry</>}
              </Button>
              <div className="text-[11px] text-[#4A5568]">Marianne charges a transparent platform fee where permitted. Fees are shown before you confirm any engagement.</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
